import {
  ensureRelatedTemplatesForOutfit,
  parseDrawDataAsImportedOutfit,
} from "AdminPanel/Spine/lib/outfitFromDrawData";
import { useConfigStore } from "AdminPanel/Spine/store/configStore";
import { useEditorStore as useSpineEditorStore } from "AdminPanel/Spine/store/editorStore";
import type { ImportedOutfitConfig } from "AdminPanel/Spine/editor/import/types";

function templateDataListFromOutfit(
  outfit: ImportedOutfitConfig,
): { templateId: string; skus: string[]; tonings?: Record<string, string>; animation?: string }[] {
  if (outfit.data && Array.isArray(outfit.data)) {
    return outfit.data;
  }
  return [];
}

/**
 * 普通模版编辑器：按混合 avatar 的 spineDrawData 加载关联模版并还原选中皮肤/染色/动画。
 * 不依赖主 Spine templateConfig（与 Spine 编辑器 applyImportedOutfitToStores 分离）。
 */
export async function applyMixedSpineOutfitInAvatarEditor(options: {
  spineDrawData: string | null | undefined;
  /** 普通模版 tid，用于 exclude 主模版 id（关联列表里不会等于它） */
  tid: string;
  onProgress?: (msg: string) => void;
}): Promise<{ ok: true } | { ok: false; message: string }> {
  const { spineDrawData, tid, onProgress } = options;
  if (spineDrawData == null || String(spineDrawData).trim() === "") {
    return { ok: true };
  }

  const raw =
    typeof spineDrawData === "string"
      ? spineDrawData
      : JSON.stringify(spineDrawData);
  const outfit = parseDrawDataAsImportedOutfit(raw);
  if (!outfit) {
    return { ok: false, message: "spineDrawData 格式无效" };
  }

  const list = templateDataListFromOutfit(outfit);
  if (list.length === 0) {
    return { ok: false, message: "spineDrawData 中无关联搭配" };
  }

  try {
    await ensureRelatedTemplatesForOutfit(outfit, tid, onProgress);
  } catch (e) {
    return {
      ok: false,
      message: e instanceof Error ? e.message : "加载关联模版失败",
    };
  }

  const { loadedRelatedTemplates } = useConfigStore.getState();
  const { setSelectedSkins, setSelectedToningColors, setCurrentAnimation, setPreviewToningId } =
    useSpineEditorStore.getState();

  const allSelectedSkins: string[] = [];
  const allTonings: Record<string, string> = {};
  let targetAnimation: string | undefined;
  let appliedAny = false;

  for (const tData of list) {
    const rel = (loadedRelatedTemplates || []).find(
      (r) => r.id === tData.templateId,
    );
    if (!rel?.config) {
      console.warn(
        `[applyMixedSpine] 未加载关联模版 ${tData.templateId}，跳过`,
      );
      continue;
    }

    const skuIdToSkinName = new Map<string, string>();
    for (const [skuId, sku] of Object.entries(rel.config.skus || {})) {
      const skinName = (sku as { skinName?: string })?.skinName;
      if (skinName) skuIdToSkinName.set(skuId, skinName);
    }

    for (const skuId of tData.skus || []) {
      const skinName = skuIdToSkinName.get(skuId);
      if (skinName) {
        allSelectedSkins.push(skinName);
        appliedAny = true;
      } else {
        console.warn(
          `[applyMixedSpine] 模版 ${tData.templateId} 未找到 SKU: ${skuId}`,
        );
      }
    }

    if (tData.tonings) {
      Object.assign(allTonings, tData.tonings);
    }
    if (tData.animation && !targetAnimation) {
      targetAnimation = tData.animation;
    }
  }

  if (!appliedAny && list.length > 0) {
    return {
      ok: false,
      message: "未能匹配到任何关联皮肤，请确认关联模版已配置 SKU",
    };
  }

  setSelectedSkins(allSelectedSkins);
  setSelectedToningColors(allTonings);
  setPreviewToningId(Object.keys(allTonings)[0] || null);
  if (targetAnimation) {
    setCurrentAnimation(targetAnimation);
  }

  return { ok: true };
}
